// Grouping and formatting for "trips" built out of individual TaskPogoTrip
// legs. A leg is one row in the database (date / days / from / to / distance);
// legs that share a Trip Name form a single trip. Legs with no name each stand
// alone as their own single-leg group.
//
// This module is also the single source of truth for the DATE CHAIN: a leg has
// no order column, so its position within a trip is expressed purely through
// `date`, and a leg's `days` (how long you stay there) is what dates the next
// one -- date[i+1] = startOfDay(date[i]) + days[i]. Adding, deleting, moving,
// or re-dating a leg re-derives every LATER leg's date from that rule
// (cascadeLegDates / cascadeAfterDateChange). The async side of that -- reading
// a group's legs and writing the new dates -- lives in
// src/utilities/TripSchedule.mjs; everything here is pure.
//
// A leg APPENDED to a trip has to land where a cascade would have put it, so
// the CREATE input for one (nextLegDefaults) is derived here too, off the same
// rule -- it is shared by the group page's Add leg and the leg page's Add Next
// Leg, which is the only reason this module reaches into TripRoute.mjs.
//
// Pure (no I/O), so it's shared by the browser (trips list, item card, group
// page), the admin migration script, and unit-testable in isolation.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import { tripTitle, tripDateMs, legDays } from './TripFields.mjs';
import { databaseDateTimeString } from './Common.mjs';
import { resolvePlaceName } from './PlaceFields.mjs';
import {
  KEY_LEG,
  tripKeyFor,
  tripGroupsForKey,
  isNamedGroupKey,
  isTripGroupIdKey,
  groupNameFromKey,
  tripGroupIdFromKey,
  tripIdentityDefaults,
  newTripGroupId,
} from './TripIdentity.mjs';
import {
  startWaypointFromPreviousLeg,
  upsertAnchor,
  mergeRouteIntoFields,
} from './TripRoute.mjs';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

// A stable key identifying the group a leg belongs to:
//   'g:<tripGroupId>'     for a leg bound to a trip (the durable answer),
//   'n:<lowercased name>' for a named leg written before ids existed (legs
//                         sharing a name group together, case-insensitively),
//   't:<id>'              for an unnamed leg (each stands alone).
//
// The precedence, and why an id-less leg never joins an id-bearing trip, are
// TripIdentity's -- stated once there and shared with the Captain's Log, which
// keys its entries by the same rule.
export function tripGroupKey(trip) {
  return tripKeyFor({
    name: trip?.name,
    tripGroupId: trip?.tripGroupId,
    ownId: trip?.id,
    alonePrefix: KEY_LEG,
  });
}

// Re-exported from TripIdentity so a caller that already imports this module
// for `groupTrips` doesn't need to know the key rule lives elsewhere.
export {
  isNamedGroupKey,
  isTripGroupIdKey,
  groupNameFromKey,
  tripGroupIdFromKey,
  tripIdentityDefaults,
  newTripGroupId,
};

// findTripGroup / tripGroupsForKey bound to this module's own sort, so every
// caller resolving an ambiguous legacy name key gets candidates in the order
// the trips list already shows trips in (newest first) rather than in whatever
// order the legs happened to arrive.
export function tripGroupsForGroupKey(groups, key) {
  return tripGroupsForKey(groups, key, { compare: (found) => sortTripGroups(found, 'desc') });
}

export function findTripGroup(groups, key) {
  const found = tripGroupsForGroupKey(groups, key);
  return found.length > 0 ? found[0] : null;
}

// The first non-empty, trimmed value for `key` scanning legs front-to-back.
function firstNonEmpty(legs, key) {
  for (const leg of legs) {
    const v = leg?.[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

// ...and back-to-front, for the trip's ending endpoint.
function lastNonEmpty(legs, key) {
  for (let i = legs.length - 1; i >= 0; i--) {
    const v = legs[i]?.[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return null;
}

function sumFinite(legs, key) {
  let total = 0;
  for (const leg of legs) {
    const n = Number(leg?.[key]);
    if (Number.isFinite(n)) total += n;
  }
  return total;
}

// The last day the trip is still happening: the final leg's date plus its stay,
// minus one, because the leg's own date is the first of those days. A day trip
// (one leg, one day) is therefore just its own date, exactly as before `days`
// existed -- which is what keeps the commonest group's date range a single date
// rather than a two-day span.
//
// Computed in UTC to match tripGroupDateRange, which parses in UTC on purpose
// (see its comment): a date stored as UTC midnight -- what parseTripExport's
// toISOString writes -- floors to the PREVIOUS local day in the Americas, so
// local-midnight arithmetic here would render a day early. Adding whole days in
// UTC also leaves the local clock time alone, so isUpcomingTripGroup's local
// start-of-day comparison still lands on the right day.
function throughDateFor(lastLeg) {
  const end = lastLeg?.date;
  if (!end) return null;
  const e = dayjs.utc(end);
  if (!e.isValid()) return null;
  return e.add(legDays(lastLeg) - 1, 'day').toISOString();
}

// The display name for a group: the first-seen spelling of the shared name for
// a named group, or the leg's own title (from - to) for an unnamed single leg.
function groupName(legs, placeIndex) {
  for (const leg of legs) {
    if (leg?.name != null && String(leg.name).trim() !== '') {
      return String(leg.name).trim();
    }
  }
  // Only THIS branch is place-shaped, which is why the index has to come in
  // here rather than be applied to `group.name` at render: by then a user's
  // trip name and a from-to fallback are the same string.
  return tripTitle(legs[0], { placeIndex });
}

function buildGroup(key, legs, placeIndex) {
  const sorted = [...legs].sort((a, b) => tripDateMs(a) - tripDateMs(b));
  return {
    key,
    // The trip's own id, or null for a group keyed by name or by a lone leg.
    // Read off the key rather than off a leg so it cannot disagree with it.
    tripGroupId: tripGroupIdFromKey(key),
    // Name from the original (input) order so it's the first-seen spelling,
    // independent of how the legs sort by date.
    name: groupName(legs, placeIndex),
    legs: sorted,
    startDate: sorted[0]?.date ?? null,
    // The last leg's own stored date. `throughDate` is the one to display or
    // compare against "now" -- endDate stays the raw value so callers that want
    // "when does the last leg start" still have it.
    endDate: sorted[sorted.length - 1]?.date ?? null,
    throughDate: throughDateFor(sorted[sorted.length - 1]),
    from: firstNonEmpty(sorted, 'from'),
    to: lastNonEmpty(sorted, 'to'),
    totalDistance: sumFinite(sorted, 'distance'),
    totalDays: sorted.reduce((n, leg) => n + legDays(leg), 0),
    hasNotes: sorted.some(
      (l) => typeof l?.notes === 'string' && l.notes.trim() !== ''
    ),
  };
}

// Group an array of trip legs into trips. Group order follows first appearance
// of each key in the input; callers that want date order should sortTripGroups.
// `placeIndex` is optional and only reaches an UNNAMED group's from-to name
// (see groupName). `group.from` / `group.to` stay the raw stored strings --
// tripGroupSummary shortens those at render, where a caller can still get at
// the originals.
export function groupTrips(trips, { placeIndex } = {}) {
  const map = new Map();
  for (const trip of trips || []) {
    const key = tripGroupKey(trip);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(trip);
  }
  return [...map.entries()].map(([key, legs]) => buildGroup(key, legs, placeIndex));
}

// The same group with its legs swapped back for the STORED rows they were
// built from, matched by id.
//
// A list groups DISPLAY copies of its legs (tripWithResolvedEndpoints, so a
// renamed place reaches the row) but hands the group it built to TripGroup
// through router state, where `group.legs` seeds the legs that page writes
// from. Passing the display copies would put a name the gazetteer supplied,
// rather than one the record holds, into a write -- the same reason
// TripGroup's own onDetails re-looks-up the stored leg before navigating.
//
// The group's own from/to/name are display values and stay as they are: only
// `legs` is ever read back out of the handover.
export function groupWithStoredLegs(group, storedLegs) {
  if (!group?.legs) return group;
  const byId = new Map((storedLegs || []).filter((leg) => leg?.id).map((leg) => [leg.id, leg]));
  return { ...group, legs: group.legs.map((leg) => byId.get(leg?.id) ?? leg) };
}

function roundNm(n) {
  return Math.round(n * 10) / 10;
}

// A one-line summary of a group's distance and endpoints, e.g.
// "125nm from Roche Harbor to Nanaimo". Degrades gracefully: "125nm",
// "from X to Y", "to Y", or '' when nothing is known.
export function tripGroupSummary(group, { placeIndex } = {}) {
  const distance =
    group?.totalDistance > 0 ? `${roundNm(group.totalDistance)}nm` : '';
  // One line under a heading, so the gazetteer's short names when a caller
  // supplies an index; the full stored strings when it doesn't.
  const from = resolvePlaceName(group?.from, placeIndex);
  const to = resolvePlaceName(group?.to, placeIndex);
  let route = '';
  if (from && to) {
    route = `from ${from} to ${to}`;
  } else if (from) {
    route = `from ${from}`;
  } else if (to) {
    route = `to ${to}`;
  }
  return [distance, route].filter(Boolean).join(' ');
}

// A human date range for a group, parsed in UTC so a UTC-midnight date buckets
// by its calendar day rather than shifting locally. Single day -> "Jun 1,
// 2027"; same year -> "Jun 1 - Jun 8, 2027"; otherwise both dates in full.
//
// Ends on the last day you're still at the final destination (throughDate), not
// the day the last leg starts -- a trip that arrives Jun 8 and stays three days
// runs through Jun 10. Falls back to endDate for a group built before
// throughDate existed (they travel through router state).
export function tripGroupDateRange(group) {
  const start = group?.startDate;
  if (!start) return '';
  const s = dayjs.utc(start);
  if (!s.isValid()) return '';
  const e = dayjs.utc(group?.throughDate ?? group?.endDate ?? start);
  if (!e.isValid() || s.format('YYYY-MM-DD') === e.format('YYYY-MM-DD')) {
    return s.format('ll');
  }
  if (s.year() === e.year()) {
    return `${s.format('MMM D')} - ${e.format('ll')}`;
  }
  return `${s.format('ll')} - ${e.format('ll')}`;
}

// True when the group is still running as of the start of `now`'s local day --
// an in-progress trip (started earlier, not yet ended) still counts as
// upcoming. Mirrors isUpcomingTrip's local-day semantics, keyed on the last day
// spent at the final destination (throughDate) so a trip doesn't drop off the
// item card while the boat is still there.
export function isUpcomingTripGroup(group, now = new Date()) {
  const end = group?.throughDate ?? group?.endDate;
  if (!end) return false;
  const date = new Date(end);
  if (Number.isNaN(date.getTime())) return false;
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  return date >= startOfDay;
}

// A DAY INDEX is a whole number of calendar days since 1970-01-01, with no
// timezone of its own. Three readings project onto it, and which one you want
// depends on what you are asking about:
//
//   localDayIndex -- the day a WALL CLOCK is showing. displayDate renders in
//                    this zone, and it's what "today" means.
//   utcDayIndex   -- the day a STORED DATE belongs to, the reading
//                    throughDateFor and tripGroupDateRange already use (a date
//                    stored as UTC midnight buckets by its own calendar day
//                    rather than flooring to the previous local one).
//   zonedDayIndex -- the day a wall clock in a NAMED zone is showing.
//                    localDayIndex is this with the zone left to the host,
//                    which is right in a browser and wrong in a Lambda (they
//                    run at TZ=UTC). "Sunday's tides" for a Pacific boater must
//                    not come out meaning Sunday UTC.
//
// All three land on the same axis, so they compare directly. Subtracting two of
// any of them gives the number of nights between them exactly, which
// `dayjs.diff(..., 'day')` does not: that divides elapsed milliseconds by 24h,
// so a span crossing a spring-forward is 167 hours and truncates to one day
// short.
//
// A missing date yields null rather than today: `dayjs(undefined)` and
// `dayjs.utc(undefined)` are both NOW, which is how a blank date silently
// became "this year" in entryYear.
export function localDayIndex(date) {
  if (date == null || date === '') return null;
  const d = dayjs(date);
  if (!d.isValid()) return null;
  return Date.UTC(d.year(), d.month(), d.date()) / 86400000;
}

export function utcDayIndex(date) {
  if (date == null || date === '') return null;
  const d = dayjs.utc(date);
  if (!d.isValid()) return null;
  return Date.UTC(d.year(), d.month(), d.date()) / 86400000;
}

// The same day, spelled `YYYY-MM-DD` -- a sortable, machine-facing label for the
// day utcDayIndex names as a number. Here rather than at its caller so the two
// readings of "which day is this" cannot disagree: a `.slice(0, 10)` on the
// stored string happens to work today and stops the moment anything writes a
// date with an offset instead of a `Z`.
//
// Used to name the per-day folders inside a downloaded photo zip. A missing or
// unparseable date yields null, never today, for localDayIndex's reason above.
export function utcDateKey(date) {
  if (date == null || date === '') return null;
  const d = dayjs.utc(date);
  if (!d.isValid()) return null;
  return d.format('YYYY-MM-DD');
}

// Intl rather than dayjs/plugin/timezone: the plugin needs a full-ICU build and
// goes quietly wrong on a small-icu one, where the constructor below throws
// instead. Loud beats silent for something a reader can't check.
//
// formatToParts, not format(): the y/m/d order of a formatted string is
// locale-dependent and parsing it back is a trap.
//
// Formatters are cached because building one is the expensive part and an email
// asks the same question for every tide event it prints. An unusable zone
// caches `null` and falls back to localDayIndex -- a bad stored zone degrades
// one email's clock rather than killing the send.
const ZONE_FORMATTERS = new Map();

export function zonedDayIndex(date, timeZone) {
  if (!timeZone) return localDayIndex(date);
  if (date == null || date === '') return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  if (!ZONE_FORMATTERS.has(timeZone)) {
    let formatter = null;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });
    } catch {
      console.warn(`Unknown time zone ${timeZone}; falling back to the host's clock.`);
    }
    ZONE_FORMATTERS.set(timeZone, formatter);
  }
  const formatter = ZONE_FORMATTERS.get(timeZone);
  if (!formatter) return localDayIndex(date);

  const parts = Object.fromEntries(
    formatter.formatToParts(d).map((part) => [part.type, part.value])
  );
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return localDayIndex(date);
  }
  return Date.UTC(year, month - 1, day) / 86400000;
}

// Per-leg `days` read off the gaps the legs already have: the whole days from
// each leg to the next, and 1 for the last (nothing follows it to measure
// against). `legs` must already be in date order.
//
// This is how legs that predate the field -- stored rows, older export files --
// get values that reproduce the schedule they already have. Stamping 1
// everywhere instead would leave a trip with week-long stays looking correct
// until the first edit, then collapse it to consecutive days the moment
// anything cascaded.
export function inferLegDays(legs) {
  const list = legs || [];
  return list.map((leg, i) => {
    if (i >= list.length - 1) return 1;
    const from = localDayIndex(leg?.date);
    const to = localDayIndex(list[i + 1]?.date);
    if (from == null || to == null) return 1;
    return Math.max(1, to - from);
  });
}

// Re-derive the dates of every leg AFTER `fromIndex` from the days chain:
// date[i+1] = startOfLocalDay(date[i]) + days[i]. `legs` must already be in the
// order the trip should run in -- this function fixes dates, never order.
//
// The leg at `fromIndex` is the anchor and is never rewritten. Its own
// time-of-day is left alone too: a stub created mid-afternoon keeps that
// instant, and flooring to the start of its day before adding days still puts
// the next leg strictly after it. Normalizing it would be a write the user
// didn't ask for on a leg they weren't editing.
//
// An anchor with no usable date yields no updates rather than a cascade off
// tripDateMs's 0 fallback, which would re-date the whole trip to 1970.
//
// Returns { legs, updates } -- the re-dated legs, and ONLY those whose instant
// actually changed, ready for the caller to persist. Neither the input array
// nor its leg objects are mutated.
export function cascadeLegDates(legs, { fromIndex = 0 } = {}) {
  const next = [...(legs || [])];
  const start = Math.max(0, fromIndex);
  const updates = [];
  if (start >= next.length - 1) return { legs: next, updates };

  const anchor = next[start];
  if (!anchor?.date || Number.isNaN(new Date(anchor.date).getTime())) {
    return { legs: next, updates };
  }

  let cursor = dayjs(anchor.date).startOf('day');
  for (let i = start; i < next.length - 1; i += 1) {
    // dayjs's add(n, 'day') is calendar-aware, so a stay spanning a DST change
    // is still the same number of days on the calendar.
    cursor = cursor.add(legDays(next[i]), 'day');
    const leg = next[i + 1];
    // Compare instants, not strings: AppSync can hand back a different ISO
    // spelling of the same moment than the one we sent.
    if (cursor.valueOf() === tripDateMs(leg)) continue;
    const date = databaseDateTimeString(cursor.toDate());
    updates.push({ id: leg.id, date });
    next[i + 1] = { ...leg, date };
  }

  return { legs: next, updates };
}

// The CREATE input for the leg that follows `previousLeg`: it picks up where
// that leg left off, so it starts once that leg's stay is over and departs from
// wherever that leg arrived. Everything here is persisted at create time rather
// than on the first save of the editor, so the new leg keeps its place in the
// trip even if the page it opens is left untouched.
//
// The date is the days chain's own rule, spelled exactly as cascadeLegDates
// spells it -- a leg dropped in at the end must land where a cascade would have
// put it, or the first edit anywhere in the trip would move it. A previous leg
// with no usable date yields no `date` at all, leaving the caller's default
// (createTripWithUI dates a stub `now`) rather than an Invalid Date.
//
// `days: 1` is stored rather than left null so the new row reads as a day's
// stay in the legs table instead of an empty cell that behaves like 1 anyway.
//
// "Where that leg arrived" is a name AND a position: if the previous leg's End
// is on the map, the new leg opens with its Start already placed there rather
// than making the user locate a point that was located a moment ago. The
// carried pin's name is the one that goes in the `from` column, so the Start row
// and the pin can't disagree -- except for an auto-generated coordinate name,
// which is the app's rendering of the position and never a column value (see
// TripRoute.mjs's anchorName); there the column falls back to the previous leg's
// `to`, exactly as it did before there were routes at all.
//
// `name` and `tripGroupId` are what keep the new leg in the same trip. Both are
// parameters rather than reads off `previousLeg` because the caller knows the
// GROUP's answer and this leg may hold another: the name because the caller
// knows the first-seen spelling, and the id because the previous leg may predate
// it -- a legacy row sitting in a trip whose other legs are already bound.
export function nextLegDefaults(previousLeg, { name, tripGroupId } = {}) {
  const defaults = { days: 1 };

  // Reject a missing date BEFORE dayjs sees it: `dayjs(undefined)` is *now*,
  // which would silently date the new leg today instead of leaving the caller's
  // default to say so.
  const previousDate = previousLeg?.date;
  const start = previousDate ? dayjs(previousDate) : null;
  if (start && start.isValid()) {
    defaults.date = databaseDateTimeString(
      start.startOf('day').add(legDays(previousLeg), 'day').toDate()
    );
  }

  const startWaypoint = startWaypointFromPreviousLeg(previousLeg);
  const carriedName =
    startWaypoint && !startWaypoint.autoName ? (startWaypoint.name || '').trim() : '';
  const from = carriedName || String(previousLeg?.to ?? '').trim();
  if (from !== '') {
    defaults.from = from;
  }
  if (startWaypoint) {
    // A brand-new leg, so there is no blob to merge into -- but the route still
    // goes through TripRoute rather than being hand-assembled here.
    defaults.fields = mergeRouteIntoFields(null, upsertAnchor(null, startWaypoint));
  }

  // Never an id without a name -- tripIdentityDefaults is the one place that
  // rule is applied, shared with the group page's own Add leg. Both values are
  // the caller's; nothing is inherited from `previousLeg` here, so a caller
  // that knows only half the answer gets a leg that says only half of it rather
  // than one silently filed under whichever trip its predecessor was in.
  Object.assign(defaults, tripIdentityDefaults({ name, tripGroupId }));

  return defaults;
}

// The cascade for "the user just re-dated one leg". `legs` carries the leg's
// NEW date already (it's saved by the time this runs); `previousDate` is what
// it held before, which is the only way to know where it stood in the trip.
//
// Moving a date later keeps the leg where it is and drags everything after it
// along -- that's "shift my trip a month", the commonest reason to touch a
// date. Moving it earlier past the leg before it re-slots it and cascades from
// there, dragging the legs it jumped. The alternative -- always re-sorting --
// breaks the first case: re-dating leg 1 later would sort it to the end and
// leave the rest of the trip behind. The alternative of never re-sorting leaves
// a non-ascending array, an invisible reorder that only surfaces on reload.
//
// Legs before the edited one are never touched, so after a mid-trip date edit
// the previous leg's `days` no longer equals the gap to it. That's deliberate:
// absorbing the difference would rewrite a value the user didn't type.
export function cascadeAfterDateChange(legs, legId, { previousDate } = {}) {
  const previousMs = previousDate ? new Date(previousDate).getTime() : NaN;
  // Order by where each leg stood BEFORE the edit, so "did it move" is a
  // question about positions rather than about the new dates.
  const order = [...(legs || [])].sort((a, b) => {
    const ka = a?.id === legId && !Number.isNaN(previousMs) ? previousMs : tripDateMs(a);
    const kb = b?.id === legId && !Number.isNaN(previousMs) ? previousMs : tripDateMs(b);
    return ka - kb;
  });

  const index = order.findIndex((leg) => leg?.id === legId);
  if (index < 0) return { legs: order, updates: [] };

  const movedMs = tripDateMs(order[index]);
  if (index === 0 || movedMs >= tripDateMs(order[index - 1])) {
    return cascadeLegDates(order, { fromIndex: index });
  }

  const [moved] = order.splice(index, 1);
  let slot = 0;
  while (slot < order.length && tripDateMs(order[slot]) <= movedMs) slot += 1;
  order.splice(slot, 0, moved);
  return cascadeLegDates(order, { fromIndex: slot });
}

// Move the leg at `index` one place earlier (delta < 0) or later (delta > 0),
// rewriting dates so the new on-screen order IS the date order. A leg has no
// order column: position within a trip is expressed purely through `date`
// (see buildGroup's sort), so a reorder has to be a date edit.
//
// The leg that lands in the earlier of the two positions takes the date that
// position held, and the days chain re-dates everything after it. Because the
// swapped pair's `days` still sum to the same number, legs beyond the pair keep
// the dates they had -- a move is a local edit, not a trip-wide rewrite -- and
// moving a leg back is exactly the inverse of moving it forward.
//
// Returns { legs, updates } -- the reordered legs, and ONLY those whose
// instant actually changed, ready for the caller to persist. A move off either
// end (or an unknown index) returns the sorted legs and no updates. Neither the
// input array nor its leg objects are mutated.
export function reorderLegsByDate(legs, index, delta) {
  const ordered = [...(legs || [])].sort((a, b) => tripDateMs(a) - tripDateMs(b));
  // `delta` is a direction, so 0 is "nowhere" rather than a silent "down".
  const target = delta === 0 ? index : index + (delta < 0 ? -1 : 1);
  if (index < 0 || index >= ordered.length || target < 0 || target >= ordered.length
      || target === index) {
    return { legs: ordered, updates: [] };
  }

  const moved = [...ordered];
  [moved[index], moved[target]] = [moved[target], moved[index]];

  // Anchor on the DATE the earlier position held, not on the date the leg now
  // standing there brought with it -- otherwise moving the second leg up would
  // start the whole trip a day later.
  const first = Math.min(index, target);
  const anchorDate = ordered[first].date;
  const updates = [];
  if (tripDateMs({ date: anchorDate }) !== tripDateMs(moved[first])) {
    updates.push({ id: moved[first].id, date: anchorDate });
    moved[first] = { ...moved[first], date: anchorDate };
  }

  const cascaded = cascadeLegDates(moved, { fromIndex: first });
  return { legs: cascaded.legs, updates: [...updates, ...cascaded.updates] };
}

// A sorted copy of `groups`. 'desc' (default) is newest-first by the last day
// of the trip; 'asc' is soonest-first by start date. Does not mutate the input.
export function sortTripGroups(groups, direction = 'desc') {
  const asc = direction === 'asc';
  const dir = asc ? 1 : -1;
  return [...(groups || [])].sort((a, b) => {
    const ka = new Date(asc ? a.startDate : (a.throughDate ?? a.endDate)).getTime();
    const kb = new Date(asc ? b.startDate : (b.throughDate ?? b.endDate)).getTime();
    return dir * (ka - kb);
  });
}
